import React, { useEffect, useState } from 'react';
import { Building2, Plus, RefreshCw, CheckCircle, XCircle, ExternalLink, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const PLAN_MAP = {
  pro:        { label: 'Pro',        color: 'var(--accent)', bg: '#E6F1FB' },
  enterprise: { label: 'Enterprise', color: '#6B3FA0', bg: '#F5F0FF' },
};

// Módulos gateables por tenant (entitlements). Todo lo demás = básico.
const MODULES = [
  { key: 'incidents',  label: 'Incidentes' },
  { key: 'ai',         label: 'IA' },
  { key: 'automation', label: 'Automatización' },
  { key: 'mdm',        label: 'MDM' },
  { key: 'cloud',      label: 'Cloud' },
  { key: 'disponibilidad', label: 'Disponibilidad' },
  { key: 'parches',        label: 'Gestión de Parches' },
];

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newTenant, setNewTenant] = useState(null);
  const [form, setForm] = useState({ name: '', subdomain: '', plan: 'pro', allowed_domains: '' });

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    fetch(`${API_URL}/tenants`, { headers })
      .then(r => r.json())
      .then(data => setTenants(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/tenants`, { method: 'POST', headers, body: JSON.stringify(form) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      setNewTenant(data);
      setShowModal(false);
      setForm({ name: '', subdomain: '', plan: 'pro', allowed_domains: '' });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (t) => {
    try {
      await fetch(`${API_URL}/tenants/${t.id}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ is_active: !t.is_active })
      });
      load();
    } catch (e) { alert(e.message); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const [demoSim, setDemoSim] = useState(null);   // { tenantId, active, total, online, last_seen }
  const [demoBusy, setDemoBusy] = useState(false);
  const loadDemoSim = async (tenantId) => {
    try {
      const r = await fetch(`${API_URL}/tenants/${tenantId}/demo-sim`, { headers });
      const d = await r.json();
      if (r.ok) setDemoSim({ tenantId, ...d });
    } catch { /* noop */ }
  };
  useEffect(() => {
    const demo = tenants.find(t => t.subdomain === 'demo');
    if (demo) loadDemoSim(demo.id);
  }, [tenants]); // eslint-disable-line
  const toggleDemoSim = async () => {
    if (!demoSim) return;
    setDemoBusy(true);
    try {
      const r = await fetch(`${API_URL}/tenants/${demoSim.tenantId}/demo-sim`, {
        method: 'PUT', headers, body: JSON.stringify({ active: !demoSim.active }),
      });
      if (r.ok) await loadDemoSim(demoSim.tenantId);
    } catch { /* noop */ }
    finally { setDemoBusy(false); }
  };

  const [savingFeat, setSavingFeat] = useState(null);
  const toggleFeature = async (t, key) => {
    const cur = (t.features || {})[key] === true;
    setSavingFeat(`${t.id}:${key}`);
    try {
      const r = await fetch(`${API_URL}/tenants/${t.id}/features`, {
        method: 'PATCH', headers, body: JSON.stringify({ features: { [key]: !cur } }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Error'); }
      // Actualizar en memoria sin recargar todo
      setTenants(prev => prev.map(x => x.id === t.id
        ? { ...x, features: { ...(x.features || {}), [key]: !cur } } : x));
    } catch (e) { alert(e.message); }
    finally { setSavingFeat(null); }
  };

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Building2 size={22} style={{ opacity: 0.7 }} /> Empresas clientes
          </div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Gestión de tenants — ITIL Suite multiempresa</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowModal(true); setError(''); setNewTenant(null); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={15} /> Nueva empresa
        </button>
      </div>

      {/* Resultado de creación */}
      {newTenant && (
        <div style={{ background: 'var(--green-dim)', border: '1px solid #8DD5A0', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--green)', marginBottom: 12 }}>
            ✓ Empresa "{newTenant.tenant?.name}" creada correctamente
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {newTenant.next_steps?.map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--green)', fontFamily: 'monospace', background: 'var(--surface)', padding: '4px 10px', borderRadius: 6, border: '1px solid #8DD5A0', flex: 1 }}>{step}</span>
                <button onClick={() => copyToClipboard(step.split(': ')[1] || step)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green)' }}>
                  <Copy size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Control del simulador de demo */}
      {demoSim && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>🧪 Simulador de demo</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
              {demoSim.total ?? 0} equipos · {demoSim.online ?? 0} online (últimos 10 min) · último reporte: {demoSim.last_seen ? new Date(demoSim.last_seen).toLocaleString('es-AR') : '—'}
            </div>
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
            background: demoSim.active ? 'var(--green-dim)' : 'var(--bg)', color: demoSim.active ? 'var(--green)' : 'var(--ink-2)',
            border: `1px solid ${demoSim.active ? '#8DD5A0' : 'var(--border-2)'}` }}>
            {demoSim.active ? 'Activo' : 'Pausado'}
          </span>
          <button className="btn btn-secondary" onClick={toggleDemoSim} disabled={demoBusy}
            style={{ color: demoSim.active ? 'var(--red)' : 'var(--green)' }}>
            {demoBusy ? '…' : (demoSim.active ? 'Pausar' : 'Activar')}
          </button>
          <button className="btn btn-secondary" onClick={() => loadDemoSim(demoSim.tenantId)} title="Refrescar estado">
            <RefreshCw size={14} />
          </button>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--gray-100)' }}>
          <button className="btn btn-secondary" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Empresa</th>
                <th style={{ width: 150 }}>Subdominio</th>
                <th style={{ width: 100 }}>Plan</th>
                <th style={{ width: 280 }}>Módulos habilitados</th>
                <th style={{ width: 200 }}>Dominios permitidos</th>
                <th style={{ width: 100 }}>Estado</th>
                <th style={{ width: 120 }}>Creado</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
              ) : tenants.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>Sin empresas registradas.</td></tr>
              ) : tenants.map(t => {
                const plan = PLAN_MAP[t.plan] || PLAN_MAP.pro;
                return (
                  <tr key={t.id} style={{ opacity: t.is_active ? 1 : 0.5 }}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)', fontFamily: 'monospace' }}>{t.db_schema}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--blue)' }}>{t.subdomain}.itil.tier5.com.ar</span>
                        <a href={`https://${t.subdomain}.itil.tier5.com.ar`} target="_blank" rel="noreferrer" style={{ color: 'var(--blue)' }}>
                          <ExternalLink size={11} />
                        </a>
                      </div>
                    </td>
                    <td>
                      <span style={{ color: plan.color, fontSize: 11, fontWeight: 500 }}>
                        {plan.label}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {MODULES.map(m => {
                          const on = (t.features || {})[m.key] === true;
                          const busy = savingFeat === `${t.id}:${m.key}`;
                          return (
                            <button key={m.key} onClick={() => toggleFeature(t, m.key)} disabled={busy}
                              title={on ? `Deshabilitar ${m.label}` : `Habilitar ${m.label}`}
                              style={{
                                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12, cursor: 'pointer',
                                border: on ? '1px solid #8DD5A0' : '1px solid var(--gray-200, #ddd)',
                                background: on ? 'var(--green-dim)' : 'transparent',
                                color: on ? 'var(--green)' : 'var(--gray-500)', opacity: busy ? 0.5 : 1,
                              }}>
                              {on ? '✓ ' : ''}{m.label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t.allowed_domains || '—'}</td>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: t.is_active ? 'var(--green)' : 'var(--red)' }}>
                        {t.is_active ? <CheckCircle size={13} /> : <XCircle size={13} />}
                        {t.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{new Date(t.created_at).toLocaleDateString('es-AR')}</td>
                    <td>
                      {t.subdomain !== 'itil' && (
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => handleToggle(t)}
                          style={{ color: t.is_active ? '#D45D00' : 'var(--green)' }}>
                          {t.is_active ? 'Desactivar' : 'Activar'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal nueva empresa */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="card" style={{ width: '100%', maxWidth: 500, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 600 }}>Nueva empresa cliente</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>✕</button>
            </div>

            {error && <div style={{ background: 'var(--red-dim)', border: '1px solid #F5C0BC', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', marginBottom: 16 }}>{error}</div>}

            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">Nombre de la empresa *</label>
                <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="Ej: Empresa SA" />
              </div>
              <div className="form-group">
                <label className="form-label">Subdominio *</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <input className="form-input" value={form.subdomain}
                    onChange={e => setForm(f => ({ ...f, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                    required placeholder="empresasa"
                    style={{ borderRadius: '8px 0 0 8px', flex: 1 }} />
                  <span style={{ background: '#f0f4f8', border: '1px solid #ddd', borderLeft: 'none', padding: '9px 12px', borderRadius: '0 8px 8px 0', fontSize: 12, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
                    .itil.tier5.com.ar
                  </span>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Plan *</label>
                <select className="form-input" value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Dominios permitidos para login</label>
                <input className="form-input" value={form.allowed_domains}
                  onChange={e => setForm(f => ({ ...f, allowed_domains: e.target.value }))}
                  placeholder="empresa.com,empresa.com.ar" />
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>Separados por coma. Ej: empresa.com,gmail.com</div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creando...' : 'Crear empresa'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
